import React, { createContext, useContext, useState } from 'react';

// ─── Translations ─────────────────────────────────────────────────────────────
const T = {
  en: {
    nav: {
      dashboard: 'Dashboard', myFamily: 'My Family',
      myCamps: 'My Camps', myCoaching: 'My Coaching',
      accountSettings: 'Account Settings', signOut: 'Sign out',
      noEnrollments: 'No enrollments yet',
    },
    dashboard: {
      section: 'Dashboard', welcome: 'Welcome back',
      progress: 'Your Progress', completed: 'completed',
      tip: 'Tip', tipText: "Start with My Family to add your spouse and children — this unlocks Camps and Coaching.",
      explore: 'Explore',
      family: { title: '1. My Family', sub: 'My spouse & children', desc: 'Manage your family members. Add your spouse and children to unlock the full portal experience.' },
      camps:  { title: '2. My Camps', sub: 'Camp enrollments', desc: 'A. If you just ENROLLED in a camp, provide all required information.\n\nB. If you ATTENDED a camp, view your results and learning profiles.' },
      coaching: { title: '3. Coaching/Mentorship', sub: 'SuperTeens Coaching - AI', desc: 'Welcome to the Coaching/Mentorship Classes. Watch all recorded lectures by Dr Ernest Wong, complete assignments and projects.' },
    },
    family: {
      section: 'My Family', title: '🏡 My Family', subtitle: 'Manage your spouse and children',
      members: 'Family Members', pending: 'Pending Invitations',
      setupComplete: 'Family setup is complete! You can now access Camps and Coaching.',
      setupPending: 'Family setup in progress',
      addChild: 'Add Child', addParent: 'Add Parent/Spouse',
      noMembers: 'No family members yet',
      invite: { child: { title: 'Invite a Child', name: "Child's Name", email: "Child's Email" },
                parent: { title: 'Invite a Parent/Spouse', name: "Spouse's Name", email: "Spouse's Email" } },
      sendInvite: 'Send Invite', sending: 'Sending...',
    },
    camps: {
      section: 'My Camps', title: '⛺ My Camps', subtitle: 'Your camp enrollments and activities',
      viewDocuments: 'View Documents', noEnrollments: 'No camp enrollments yet',
      familyRequired: 'Family Setup Required',
      familyRequiredDesc: 'Complete your family setup before accessing camps.',
      goToFamily: 'Go to Family Setup',
    },
    coaching: {
      section: 'My Coaching', title: '📘 My Coaching', subtitle: 'SuperTeens Coaching — AI Programs',
      active: 'Active Programs', completed: 'Completed Programs',
      viewLectures: 'View Lectures', viewResults: 'View Results',
      noEnrollments: 'No coaching enrollments yet',
      familyRequired: 'Family Setup Required',
    },
    documents: {
      section: 'Required Documents', back: '← Back to My Camps',
      allDone: 'All documents complete', progress: 'documents completed',
      editor: 'Edit Document', save: 'Save Document',
      saving: 'Saving...', saved: 'Saved!',
      markComplete: 'Mark as Complete', markIncomplete: 'Mark as Incomplete',
      noDocuments: 'No documents required for this camp.',
      required: 'Required', optional: 'Optional',
    },
    account: {
      title: 'Account Settings', save: 'Save Changes', saving: 'Saving...',
      cancel: 'Cancel', saved: 'Profile updated!',
      tabs: { profile: 'Profile' },
      fields: {
        name: 'Full Name', dob: 'Date of Birth', gender: 'Gender',
        phone: 'Phone', address: 'Address', street: 'Street',
        district: 'District', city: 'City',
        school: 'School Name', grade: 'Grade', tshirt: 'T-Shirt Size',
      },
    },
    common: {
      loading: 'Loading...', retry: 'Retry', cancel: 'Cancel',
      save: 'Save', complete: 'Complete', pending: 'Pending',
      active: 'Active', required: 'Required', optional: 'Optional',
      male: 'Male', female: 'Female', selectGender: 'Select gender',
      selectGrade: 'Select grade', selectSize: 'Select size',
      name: 'Name', email: 'Email', role: 'Role',
    },
  },
  vi: {
    nav: {
      dashboard: 'Trang Chủ', myFamily: 'Gia Đình Tôi',
      myCamps: 'Trại Hè', myCoaching: 'Coaching',
      accountSettings: 'Cài Đặt Tài Khoản', signOut: 'Đăng Xuất',
      noEnrollments: 'Chưa có đăng ký',
    },
    dashboard: {
      section: 'Trang Chủ', welcome: 'Chào mừng trở lại',
      progress: 'Tiến Độ Của Bạn', completed: 'hoàn thành',
      tip: 'Mẹo', tipText: 'Bắt đầu với Gia Đình Tôi để thêm vợ/chồng và con cái — điều này mở khóa Trại Hè và Coaching.',
      explore: 'Xem Ngay',
      family: { title: '1. Gia Đình Của Tôi', sub: 'Vợ/chồng và con tôi', desc: 'Quản lý thành viên gia đình. Thêm vợ/chồng và con cái để mở khóa toàn bộ tính năng.' },
      camps:  { title: '2. Trại Hè', sub: 'Đăng ký trại hè', desc: 'A. Nếu bạn vừa ĐĂNG KÝ trại, cung cấp tất cả thông tin yêu cầu.\n\nB. Nếu bạn đã THAM DỰ trại, xem kết quả và hồ sơ học tập.' },
      coaching: { title: '3. Coaching/Mentorship', sub: 'SuperTeens Coaching - AI', desc: 'Chào mừng đến với các lớp Coaching/Mentorship. Xem tất cả bài giảng của Tiến sĩ Ernest Wong, hoàn thành bài tập và dự án.' },
    },
    family: {
      section: 'Gia Đình Tôi', title: '🏡 Gia Đình Tôi', subtitle: 'Quản lý vợ/chồng và con cái',
      members: 'Thành Viên Gia Đình', pending: 'Lời Mời Đang Chờ',
      setupComplete: 'Gia đình đã hoàn thiện! Bạn có thể truy cập Trại Hè và Coaching.',
      setupPending: 'Đang thiết lập gia đình',
      addChild: 'Thêm Con', addParent: 'Thêm Phụ Huynh/Vợ/Chồng',
      noMembers: 'Chưa có thành viên gia đình',
      invite: { child: { title: 'Mời Con', name: 'Tên Con', email: 'Email Con' },
                parent: { title: 'Mời Phụ Huynh/Vợ/Chồng', name: 'Tên Vợ/Chồng', email: 'Email Vợ/Chồng' } },
      sendInvite: 'Gửi Lời Mời', sending: 'Đang gửi...',
    },
    camps: {
      section: 'Trại Hè', title: '⛺ Trại Hè', subtitle: 'Danh sách đăng ký trại hè',
      viewDocuments: 'Xem Tài Liệu', noEnrollments: 'Chưa có đăng ký trại hè nào',
      familyRequired: 'Cần Thiết Lập Gia Đình',
      familyRequiredDesc: 'Vui lòng hoàn thiện gia đình trước khi truy cập trại hè.',
      goToFamily: 'Đến Thiết Lập Gia Đình',
    },
    coaching: {
      section: 'Coaching', title: '📘 Coaching', subtitle: 'SuperTeens Coaching — AI Programs',
      active: 'Chương Trình Đang Học', completed: 'Chương Trình Đã Hoàn Thành',
      viewLectures: 'Xem Bài Giảng', viewResults: 'Xem Kết Quả',
      noEnrollments: 'Chưa có đăng ký coaching nào',
      familyRequired: 'Cần Thiết Lập Gia Đình',
    },
    documents: {
      section: 'Tài Liệu Yêu Cầu', back: '← Quay Lại Trại Hè',
      allDone: 'Đã hoàn thành tất cả tài liệu', progress: 'tài liệu hoàn thành',
      editor: 'Chỉnh Sửa Tài Liệu', save: 'Lưu Tài Liệu',
      saving: 'Đang lưu...', saved: 'Đã lưu!',
      markComplete: 'Đánh Dấu Hoàn Thành', markIncomplete: 'Đánh Dấu Chưa Xong',
      noDocuments: 'Không có tài liệu yêu cầu cho trại này.',
      required: 'Bắt buộc', optional: 'Tùy chọn',
    },
    account: {
      title: 'Cài Đặt Tài Khoản', save: 'Lưu Thay Đổi', saving: 'Đang lưu...',
      cancel: 'Hủy', saved: 'Đã cập nhật hồ sơ!',
      tabs: { profile: 'Hồ Sơ' },
      fields: {
        name: 'Họ và Tên', dob: 'Ngày Sinh', gender: 'Giới Tính',
        phone: 'Số Điện Thoại', address: 'Địa Chỉ', street: 'Đường/Phố',
        district: 'Quận/Huyện', city: 'Thành Phố',
        school: 'Tên Trường', grade: 'Lớp', tshirt: 'Cỡ Áo',
      },
    },
    common: {
      loading: 'Đang tải...', retry: 'Thử Lại', cancel: 'Hủy',
      save: 'Lưu', complete: 'Hoàn Thành', pending: 'Đang Chờ',
      active: 'Đang Hoạt Động', required: 'Bắt buộc', optional: 'Tùy chọn',
      male: 'Nam', female: 'Nữ', selectGender: 'Chọn giới tính',
      selectGrade: 'Chọn lớp', selectSize: 'Chọn cỡ',
      name: 'Tên', email: 'Email', role: 'Vai Trò',
    },
  },
};

// ─── Context ──────────────────────────────────────────────────────────────────
const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export const useTranslation = () => useContext(I18nContext);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem('lang') || 'en');

  const setLang = (l) => { localStorage.setItem('lang', l); setLangState(l); };

  const t = (key) => {
    const parts = key.split('.');
    let val = T[lang];
    for (const p of parts) val = val?.[p];
    if (val !== undefined) return val;
    // Fallback to English
    let fb = T.en;
    for (const p of parts) fb = fb?.[p];
    return fb ?? key;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};
